import { Filter } from "@/shared/lib/filter/filter";
import { Address } from "@/services/backend/addresses/types/address";
import { Contact } from "@/services/backend/contacts/types/contact";
import {
  DocumentBaseFilters,
  DocumentCommonFilters,
} from "@/services/backend/documents/types/filters";
import { OrdrBy } from "@/shared/lib/ordr/ordrBy";

import { downloadResponseHandler } from "@/services/backend/_lib/pdf-helpers";
import {
  objectURLResponseHandler,
  revokeObjectURLOptions,
} from "@/shared/lib/utilities/object-url";
import { Lst } from "@/shared/lib/lst/lst";
import { providesList } from "@/services/backend/_lib/tag-helpers";
import { backendApi } from "@/services/backend/api";
import {
  ReferenceNumberFilters,
  ResetReferenceNumberRequest,
} from "@/services/backend/documents/types/reference-number";
import { Confirmation } from "@/services/backend/rlt/confirmations/types/confirmation";
import { ConfirmationBasePatchRequest } from "@/services/backend/documents/types/confirmation";
import { updateCache } from "@/services/backend/_lib/update-cache";
import { DocumentState } from "@/services/backend/documents/types/state";

export const rltConfirmationApi = backendApi.injectEndpoints({
  endpoints: (build) => ({
    rltConfirmationCreate: build.mutation<Confirmation, CreateRequest>({
      query: (request) => ({
        url: `/rlt/confirmation`,
        method: "POST",
        body: request,
      }),
      invalidatesTags: () => [
        { type: "RltConfirmation", id: "LIST" },
        "RltConfirmation",
        "RltPricingByArea",
      ],
    }),
    rltConfirmationCreateFromOld: build.mutation<
      Confirmation,
      CreateFromOldRequest
    >({
      query: (request) => ({
        url: "/rlt/confirmation/create-from-old",
        method: "POST",
        body: request,
      }),
      invalidatesTags: () => [
        { type: "RltConfirmation", id: "LIST" },
        "RltConfirmation",
        "RltPricingByArea",
      ],
    }),
    rltConfirmationCreateFromOffer: build.mutation<
      Confirmation,
      CreateFromOfferRequest
    >({
      query: (request) => ({
        url: "/rlt/confirmation/create-from-offer",
        method: "POST",
        body: request,
      }),
      invalidatesTags: () => [
        { type: "RltConfirmation", id: "LIST" },
        "RltConfirmation",
        "RltPricingByArea",
      ],
    }),
    rltConfirmationShow: build.query<Confirmation, ShowRequest>({
      query: (request) => ({
        url: `/rlt/confirmation`,
        method: "GET",
        params: request,
      }),
      providesTags: (result) =>
        result ? [{ type: "RltConfirmation", id: result.id }] : [],
    }),
    rltConfirmationResetReferenceNumber: build.mutation<
      Confirmation,
      ResetReferenceNumberRequest
    >({
      query: (request) => ({
        url: `/rlt/confirmation/reset-reference-number`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: (_result, _e, request) => [
        { type: "RltConfirmation", id: request.id },
        { type: "RltConfirmation", id: "LIST" },
      ],
    }),
    rltConfirmationList: build.query<Lst<Confirmation>, Partial<ListRequest>>({
      query: (request) => ({
        url: `/rlt/confirmation/list`,
        method: "GET",
        params: {
          request: btoa(JSON.stringify(request)),
        },
      }),
      providesTags: (result) => providesList(result?.items, "RltConfirmation"),
    }),
    rltConfirmationPatch: build.mutation<Confirmation, PatchRequest>({
      query: (request) => ({
        url: `/rlt/confirmation`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: (_result, _e, request) => [
        { type: "RltConfirmation", id: request.id },
        "RltConfirmation",
        "RltCalculation",
        "RltTreatment",
        "RltPricing",
        "RltPricingByArea",
      ],
    }),
    rltConfirmationPdfPreview: build.query<{ objectURL: string }, PDFRequest>({
      ...revokeObjectURLOptions(),
      query: (request) => ({
        url: `/rlt/confirmation`,
        method: "GET",
        params: request,
        headers: { Accept: "application/pdf" },
        responseHandler: objectURLResponseHandler,
        // Keep the PDF out of the browser's HTTP cache.
        cache: "no-store",
      }),
    }),
    rltConfirmationPdfDownload: build.mutation<
      { filename: string; objectURL: string },
      PDFRequest
    >({
      query: (request) => ({
        url: `/rlt/confirmation`,
        method: "GET",
        params: { ...request, download: true },
        headers: { Accept: "application/pdf" },
        responseHandler: downloadResponseHandler,
        // Keep the PDF out of the browser's HTTP cache.
        cache: "no-store",
      }),
    }),
    rltConfirmationUpdateState: build.mutation<
      Confirmation,
      UpdateStateRequest
    >({
      query: (request) => ({
        url: `/rlt/confirmation/state`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: (_r, _e, request) => [
        { type: "RltConfirmation", id: request.id },
        "RltConfirmation",
      ],
    }),
    rltConfirmationDelete: build.mutation<Confirmation, DeleteRequest>({
      query: (request) => ({
        url: `/rlt/confirmation`,
        method: "DELETE",
        body: request,
      }),
      invalidatesTags: (result, e, request) => [
        { type: "RltConfirmation", id: request.id },
        "RltConfirmation",
      ],
    }),
  }),
});

export const {
  useRltConfirmationShowQuery,
  useRltConfirmationResetReferenceNumberMutation,
  useRltConfirmationCreateMutation,
  useRltConfirmationCreateFromOfferMutation,
  useRltConfirmationCreateFromOldMutation,
  useRltConfirmationListQuery,
  useRltConfirmationDeleteMutation,
  useRltConfirmationPatchMutation,
  useRltConfirmationUpdateStateMutation,
  useRltConfirmationPdfPreviewQuery,
  useLazyRltConfirmationPdfPreviewQuery,
  useRltConfirmationPdfDownloadMutation,
} = rltConfirmationApi;

export interface ShowRequest {
  id: string;
}

export interface ListRequest
  extends DocumentBaseFilters, DocumentCommonFilters, ReferenceNumberFilters {
  orderBy?: OrdrBy<string>[];
  date?: Filter<string>;
  state?: Filter<DocumentState>;
  limit?: number;
  offset?: number;
}

export interface CreateRequest {
  id: string;
  processId: string;
  companyId: string;
  branchId: string;
  customerId: string;
  serviceObjectId: string;
}

export interface CreateFromOfferRequest {
  offerId: string;
  id: string;
  processId: string;
  companyId: string;
  branchId: string;
  customerId: string;
  serviceObjectId: string;
}

export interface CreateFromOldRequest {
  oldConfirmationId: string;
  id: string;
  processId: string;
  companyId: string;
  branchId: string;
  customerId: string;
  serviceObjectId: string;
}

export interface DeleteRequest {
  id: string;
}

export type PatchRequest = {
  id: string;
  referenceNumber?: string;
  internalComment?: string;
  serviceAddress?: Address | null;
  serviceContact?: Contact | null;
} & ConfirmationBasePatchRequest;

export interface PDFRequest {
  id: string;
  document: ConfirmationDocument;
}

export interface UpdateStateRequest {
  id: string;
  state: DocumentState;
  stateSetBy: string;
}

export enum ConfirmationDocument {
  All = "all",
  Letter = "letter",
  Treatment = "treatment",
  TreatmentByArea = "treatment-by-area",
}

/**
 * Patches the cached confirmation for an optimistic update. Defined here, at module
 * scope, so it keeps one identity for the whole app — see {@link updateCache}.
 */
export const updateRltConfirmationCache = updateCache(
  rltConfirmationApi,
  "rltConfirmationShow",
);
